import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Calendar, Clock, MapPin, Users, Plus, Edit, Trash2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import StudentDetailModal from '../components/admin/StudentDetailModal';

const DAYS_OF_WEEK = ['週一', '週二', '週三', '週四', '週五', '週六', '週日'];
const PRESET_TIME_SLOTS = [
  '09:00-10:00',
  '10:00-11:00',
  '11:00-12:00',
  '14:00-15:00',
  '15:00-16:00',
  '16:00-17:00',
  '17:00-18:00',
  '18:00-19:00',
  '19:00-20:00',
  '20:00-21:00'
];

export default function ClassScheduleManagement({ user }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showStudentDetail, setShowStudentDetail] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    day_of_week: 0,
    time_slot: '',
    custom_time: '',
    location: '',
    custom_location: '',
    coach_id: '',
    student_ids: [],
    notes: ''
  });

  const [coaches, setCoaches] = useState([]);
  const [students, setStudents] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    loadSchedules();
    loadCoaches();
    loadStudents();
    loadLocations();
  }, []);

  const loadSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/class-schedules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSchedules(res.data);
    } catch (error) {
      console.error('載入課時紀錄失敗:', error);
      toast.error('載入課時紀錄失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadCoaches = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users?role=coach`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCoaches(res.data);
    } catch (error) {
      console.error('載入教練列表失敗:', error);
    }
  };

  const loadStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users?role=student`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStudents(res.data);
    } catch (error) {
      console.error('載入學生列表失敗:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/locations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLocations(res.data);
    } catch (error) {
      console.error('載入地點列表失敗:', error);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const scheduleData = {
        day_of_week: formData.day_of_week,
        time_slot: formData.custom_time || formData.time_slot,
        location: formData.custom_location || formData.location,
        coach_id: formData.coach_id,
        student_ids: formData.student_ids,
        notes: formData.notes
      };

      await axios.post(`${API}/class-schedules`, scheduleData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('課堂已創建');
      setShowCreateDialog(false);
      resetForm();
      loadSchedules();
    } catch (error) {
      console.error('創建課堂失敗:', error);
      toast.error('創建課堂失敗');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('確定要刪除這個課堂嗎？')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/class-schedules/${scheduleId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('課堂已刪除');
      setShowDetailDialog(false);
      loadSchedules();
    } catch (error) {
      console.error('刪除課堂失敗:', error);
      toast.error(error.response?.data?.detail || '刪除課堂失敗');
    }
  };

  // 刪除合併課程中的所有相關課程
  const handleDeleteMergedSchedules = async (scheduleIds) => {
    if (!window.confirm(`確定要刪除此時段的所有課堂嗎？（共 ${scheduleIds.length} 個）`)) return;
    
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      
      for (const scheduleId of scheduleIds) {
        try {
          await axios.delete(`${API}/class-schedules/${scheduleId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          successCount++;
        } catch (error) {
          console.error(`刪除課堂 ${scheduleId} 失敗:`, error);
        }
      }
      
      toast.success(`已刪除 ${successCount}/${scheduleIds.length} 個課堂`);
      setShowDetailDialog(false);
      loadSchedules();
    } catch (error) {
      console.error('刪除課堂失敗:', error);
      toast.error('刪除課堂失敗');
    }
  };

  const resetForm = () => {
    setFormData({
      day_of_week: 0,
      time_slot: '',
      custom_time: '',
      location: '',
      custom_location: '',
      coach_id: '',
      student_ids: [],
      notes: ''
    });
  };

  // 獲取指定日期的課程，並按時間段合併
  const getSchedulesForDay = (dayIndex) => {
    const daySchedules = schedules.filter(s => s.day_of_week === dayIndex);
    
    // 按時間段合併課程
    const mergedByTimeSlot = {};
    daySchedules.forEach(schedule => {
      const timeSlot = schedule.time_slot || '未指定時間';
      const location = schedule.location || '';
      const key = `${timeSlot}_${location}`;
      
      if (!mergedByTimeSlot[key]) {
        mergedByTimeSlot[key] = {
          time_slot: timeSlot,
          location: location,
          schedules: [],
          students: [],
          coach: schedule.coach,
          schedule_ids: []
        };
      }
      
      mergedByTimeSlot[key].schedules.push(schedule);
      mergedByTimeSlot[key].schedule_ids.push(schedule.schedule_id);
      
      // 合併學生（去重）
      if (schedule.students) {
        schedule.students.forEach(student => {
          if (!mergedByTimeSlot[key].students.find(s => s.user_id === student.user_id)) {
            mergedByTimeSlot[key].students.push(student);
          }
        });
      }
    });
    
    // 轉換為數組並按時間排序
    return Object.values(mergedByTimeSlot).sort((a, b) => {
      const timeA = a.time_slot || '';
      const timeB = b.time_slot || '';
      return timeA.localeCompare(timeB);
    });
  };

  // 獲取原始未合併的課程（用於詳情顯示）
  const getOriginalSchedulesForDay = (dayIndex) => {
    return schedules.filter(s => s.day_of_week === dayIndex);
  };

  const handleDayClick = (dayIndex) => {
    setSelectedDay(dayIndex);
  };

  const handleScheduleClick = (schedule) => {
    setSelectedSchedule(schedule);
    setShowDetailDialog(true);
  };

  const handleStudentClick = (studentId) => {
    setSelectedStudentId(studentId);
    setShowStudentDetail(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Calendar size={32} className="text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">課時紀錄管理</h1>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-500 hover:bg-blue-600"
          >
            <Plus size={20} className="mr-2" />
            新增課堂
          </Button>
        </div>

        {/* Weekly Calendar Grid */}
        <div className="grid grid-cols-7 gap-4 mb-6">
          {DAYS_OF_WEEK.map((day, index) => {
            const daySchedules = getSchedulesForDay(index);
            return (
              <div
                key={index}
                onClick={() => handleDayClick(index)}
                className={`bg-white rounded-lg shadow-md p-4 cursor-pointer transition-all hover:shadow-lg ${
                  selectedDay === index ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">
                  {day}
                </h3>
                <div className="space-y-2">
                  {daySchedules.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">暫無課堂</p>
                  ) : (
                    daySchedules.map((mergedSchedule, idx) => (
                      <div
                        key={`${mergedSchedule.time_slot}_${idx}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // 點擊時設置合併後的數據
                          setSelectedSchedule({
                            ...mergedSchedule.schedules[0],
                            time_slot: mergedSchedule.time_slot,
                            location: mergedSchedule.location,
                            students: mergedSchedule.students,
                            schedule_ids: mergedSchedule.schedule_ids,
                            is_merged: mergedSchedule.schedules.length > 1
                          });
                          setShowDetailDialog(true);
                        }}
                        className="bg-blue-50 p-3 rounded border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        <div className="flex items-center gap-2 text-sm mb-1">
                          <Clock size={14} className="text-blue-600" />
                          <span className="font-medium">{mergedSchedule.time_slot}</span>
                        </div>
                        {mergedSchedule.location && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                            <MapPin size={12} />
                            <span>{mergedSchedule.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Users size={14} />
                          <span>{mergedSchedule.students?.length || 0} 人</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Create Schedule Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>新增課堂</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Day of Week */}
              <div>
                <label className="block text-sm font-medium mb-2">星期</label>
                <Select
                  value={formData.day_of_week.toString()}
                  onValueChange={(value) => setFormData({...formData, day_of_week: parseInt(value)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day, index) => (
                      <SelectItem key={index} value={index.toString()}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Time Slot */}
              <div>
                <label className="block text-sm font-medium mb-2">時間</label>
                <Select
                  value={formData.time_slot}
                  onValueChange={(value) => setFormData({...formData, time_slot: value, custom_time: ''})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選擇時段" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESET_TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="或輸入自訂時間 (例: 13:30-14:30)"
                  value={formData.custom_time}
                  onChange={(e) => setFormData({...formData, custom_time: e.target.value, time_slot: ''})}
                  className="mt-2"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium mb-2">地點</label>
                <Select
                  value={formData.location}
                  onValueChange={(value) => setFormData({...formData, location: value, custom_location: ''})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選擇地點" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.location_id} value={loc.name}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="或輸入自訂地點"
                  value={formData.custom_location}
                  onChange={(e) => setFormData({...formData, custom_location: e.target.value, location: ''})}
                  className="mt-2"
                />
              </div>

              {/* Coach */}
              <div>
                <label className="block text-sm font-medium mb-2">教練</label>
                <Select
                  value={formData.coach_id}
                  onValueChange={(value) => setFormData({...formData, coach_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="選擇教練" />
                  </SelectTrigger>
                  <SelectContent>
                    {coaches.map((coach) => (
                      <SelectItem key={coach.user_id} value={coach.user_id}>
                        {coach.coach_name || coach.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Students */}
              <div>
                <label className="block text-sm font-medium mb-2">學生（可多選）</label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {students.map((student) => (
                    <label key={student.user_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={formData.student_ids.includes(student.user_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              student_ids: [...formData.student_ids, student.user_id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              student_ids: formData.student_ids.filter(id => id !== student.user_id)
                            });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-blue-700">
                        {student.student_name || student.username}
                      </span>
                      {student.student_name && (
                        <span className="text-xs text-gray-400">({student.username})</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">備註</label>
                <Input
                  placeholder="課堂備註"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  取消
                </Button>
                <Button onClick={handleCreateSchedule} className="bg-blue-500 hover:bg-blue-600">
                  創建
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Schedule Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>課堂詳情</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (selectedSchedule?.schedule_ids?.length > 1) {
                      handleDeleteMergedSchedules(selectedSchedule.schedule_ids);
                    } else {
                      handleDeleteSchedule(selectedSchedule?.schedule_id);
                    }
                  }}
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 size={18} />
                </Button>
              </DialogTitle>
            </DialogHeader>

            {selectedSchedule && (
              <div className="space-y-4">
                {/* Time and Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock size={20} className="text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">時間</p>
                      <p className="font-medium">{selectedSchedule.time_slot}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin size={20} className="text-green-600" />
                    <div>
                      <p className="text-xs text-gray-500">地點</p>
                      <p className="font-medium">{selectedSchedule.location || '未指定'}</p>
                    </div>
                  </div>
                </div>

                {/* Coach */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">教練</p>
                  <p className="font-medium">{selectedSchedule.coach?.coach_name || selectedSchedule.coach?.username || '未指定'}</p>
                </div>

                {/* Students */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users size={20} className="text-orange-600" />
                    <h3 className="font-semibold">學生名單 ({selectedSchedule.students?.length || 0})</h3>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedSchedule.students?.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">暫無學生</p>
                    ) : (
                      selectedSchedule.students?.map((student) => (
                        <div
                          key={student.user_id}
                          onClick={() => handleStudentClick(student.user_id)}
                          className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                        >
                          <div>
                            <span className="font-medium text-blue-700">
                              {student.student_name || student.username}
                            </span>
                            {student.student_name && (
                              <span className="text-xs text-gray-400 ml-2">({student.username})</span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">{student.phone || student.email}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Notes */}
                {selectedSchedule.notes && (
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-xs text-gray-500 mb-1">備註</p>
                    <p className="text-sm">{selectedSchedule.notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Student Detail Modal */}
        {showStudentDetail && (
          <StudentDetailModal
            studentId={selectedStudentId}
            onClose={() => setShowStudentDetail(false)}
          />
        )}
      </div>
    </div>
  );
}
